<template>
  <router-link class="community-topic-card d-block" :to="topicRoute">
    <div class="topic-header d-flex align-items-center">
      <div class="avatar">
        <img v-if="authorPhoto" :src="authorPhoto" :alt="authorName" />
        <span v-else class="letter">{{ authorInitial }}</span>
      </div>
      <div class="flex-fill d-flex align-items-center flex-wrap">
        <span class="author-name">{{ authorName }}</span>
        <span v-if="isModeratorAuthor" class="badge-moderator ml-2">
          <i class="fa-solid fa-shield" /> Moderador
        </span>
        <span v-if="post.is_pinned" class="badge-pinned ml-2">
          <i class="icon-star" /> Fixado
        </span>
      </div>
    </div>

    <p class="topic-preview mb-0">{{ preview }}</p>

    <div class="topic-meta d-flex align-items-center">
      <small class="topic-date">{{ createdAt }}</small>
      <span class="meta-item ml-auto">
        <i class="fa-solid fa-heart" /> {{ post.likes_count || 0 }}
      </span>
      <span class="meta-item ml-3">
        <i class="fa-solid fa-message" /> {{ post.comments_count || 0 }}
      </span>
    </div>
  </router-link>
</template>

<script>
import { timeFromNow } from "@/functions/helpers";

export default {
  name: "TopicCard",
  props: {
    post: {
      type: Object,
      required: true
    },
    forumId: {
      type: String,
      required: true
    }
  },
  computed: {
    topicRoute() {
      return {
        name: "community-topic",
        params: {
          id: this.$route.params.id,
          forumId: this.forumId,
          postId: this.post.id
        }
      };
    },
    author() {
      return this.post.author || {};
    },
    authorName() {
      return this.author.name || "Usuário";
    },
    authorPhoto() {
      return this.author.photo;
    },
    authorInitial() {
      return (this.authorName[0] || "?").toUpperCase();
    },
    isModeratorAuthor() {
      return !!this.author.is_moderator;
    },
    createdAt() {
      return timeFromNow(this.post.created_at);
    },
    preview() {
      const doc = new DOMParser().parseFromString(
        this.post.text || "",
        "text/html"
      );
      const text = (doc.body.textContent || "").trim();
      if (text) return text;
      return this.post.image ? "Publicação com imagem" : "";
    }
  }
};
</script>

<style lang="scss" scoped>
.community-topic-card {
  padding: 16px;
  border-radius: 4px;
  border: 1px solid rgba(128, 128, 128, 0.2);
  background: rgba(128, 128, 128, 0.06);
  font-family: "Inter", "Noto Sans", sans-serif;
  color: inherit;
  text-decoration: none;
  margin-bottom: 12px;
  transition: border-color 0.15s ease;

  &:hover {
    color: inherit;
    text-decoration: none;
    border-color: rgba(128, 128, 128, 0.45);
  }

  .topic-header {
    gap: 12px;
    margin-bottom: 8px;

    .avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(128, 128, 128, 0.2);
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .letter {
        font-size: 14px;
        font-weight: 500;
      }
    }

    .author-name {
      font-weight: 500;
      font-size: 14px;
    }

    .badge-pinned,
    .badge-moderator {
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 12px;
      background: rgba(128, 128, 128, 0.16);

      i {
        font-size: 12px;
      }
    }

    .badge-moderator {
      background: transparent;
      padding: 0;
      font-size: 12px;
      color: #54acbe;
    }
  }

  .topic-preview {
    font-size: 14px;
    line-height: 1.5;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .topic-meta {
    margin-top: 12px;
    font-size: 13px;
    opacity: 0.8;

    .topic-date {
      font-size: 12px;
      opacity: 0.85;
    }

    .meta-item {
      display: inline-flex;
      align-items: center;
      gap: 4px;

      i {
        font-size: 16px;
      }
    }
  }

  .theme-dark & {
    background: #0d161c;
    border-color: #07151f;

    &:hover {
      border-color: #438a98;
    }

    .topic-header .author-name {
      color: #fdfdfd;
    }

    .topic-preview {
      color: #e1e1e1;
    }

    .topic-meta {
      color: #7a8c99;
      opacity: 1;

      i {
        color: #5e6f7c;
      }
    }
  }
}
</style>
